/**
 * Unified Analytics Service for Toon House
 * 
 * This service provides a centralized way to track analytics events
 * throughout the application using both Google Analytics and Statsig.
 */
import { EVENTS, logAnalyticsEvent } from './events';

// Google Analytics measurement ID
const GA_MEASUREMENT_ID = 'G-CHP189DJ95';

class UnifiedAnalyticsService {
  constructor() {
    this.client = null;
    this.initialized = false;
    this.userProperties = {};
    this.startTime = new Date();
    this.modalOpenTime = null;
  }

  /**
   * Initialize the analytics service with Statsig client and GA4
   * @param {Object} statsigClient - Statsig client instance
   */
  initialize(statsigClient) {
    // Initialize Statsig
    this.client = statsigClient;
    
    // Initialize Google Analytics
    this.initializeGA();
    
    // Track app loaded event
    this.trackEvent(EVENTS.APP_LOADED, null, {
      load_time: new Date() - this.startTime,
      user_agent: navigator.userAgent,
      language: navigator.language,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight
    });
    
    // Set initialized flag
    this.initialized = true;
    
    // Setup performance monitoring
    this.setupPerformanceMonitoring();
    
    return this;
  }
  
  /**
   * Initialize Google Analytics
   */
  initializeGA() {
    // Add Google Analytics script tag to the document
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID);

    // Make gtag available globally
    window.gtag = gtag;
  }

  /**
   * Set user properties for analytics events
   * @param {Object} properties - User properties to set
   */
  setUserProperties(properties) {
    this.userProperties = {
      ...this.userProperties,
      ...properties
    };
  }

  /**
   * Get time on site in seconds
   * @returns {Number} Time on site in seconds
   */
  getTimeOnSite() {
    return Math.floor((new Date() - this.startTime) / 1000);
  }

  /**
   * Track an analytics event in both Statsig and GA4
   * @param {String} eventName - Name of the event from EVENTS object
   * @param {String|Number} value - Primary value associated with the event
   * @param {Object} metadata - Additional metadata to log with the event
   */
  trackEvent(eventName, value = null, metadata = {}) {
    const enrichedMetadata = {
      ...this.userProperties,
      ...metadata,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      time_on_site_seconds: this.getTimeOnSite(),
    };
    
    // Track with Statsig
    if (this.client) {
      logAnalyticsEvent(this.client, eventName, value, enrichedMetadata);
    }
    
    // Track with Google Analytics
    this.trackWithGA(eventName, value, enrichedMetadata);
  }
  
  /**
   * Track events with Google Analytics
   * @private
   */
  trackWithGA(eventName, value, metadata = {}) {
    if (!window.gtag) return;
    
    // Map events to GA4 format
    switch(eventName) {
      case EVENTS.PAGE_VIEW:
        if (value === 'voting') {
          window.gtag('event', 'vote_page_view', {
            event_category: 'Page View',
            event_label: 'Voting Page View'
          });
        } else if (value === 'confirmation') {
          window.gtag('event', 'vote_confirmation_page_view', {
            event_category: 'Page View',
            event_label: 'Confirmation Page View'
          });
        } else {
          window.gtag('event', 'screen_view', {
            screen_name: value,
            app_name: 'Toon House Voting'
          });
        }
        break;
        
      case EVENTS.CHARACTER_SELECTED:
        window.gtag('event', 'character_selected', {
          event_category: 'User Interaction',
          event_label: 'Character Selected',
          character_id: value,
          character_name: metadata.character_name || value
        });
        break;
        
      case EVENTS.FORM_INTERACTION:
        // Check if we've already logged this event in the current session
        if (!sessionStorage.getItem('form_interaction_logged')) {
          window.gtag('event', 'vote_form_started', {
            event_category: 'Form',
            event_label: 'Form Interaction Started'
          });
          
          // Mark that we've logged this event
          sessionStorage.setItem('form_interaction_logged', 'true');
        }
        break;
        
      case EVENTS.VOTE_SUBMITTED:
        window.gtag('event', 'vote_submitted', {
          event_category: 'Conversion',
          event_label: 'Vote Submitted',
          character_id: value,
          email_collected: metadata.has_email || false
        });
        break;
        
      case EVENTS.CTA_BUTTON_CLICKED:
        window.gtag('event', 'confirmation_cta_click', {
          event_category: 'Engagement',
          event_label: 'CTA Interaction',
          cta_type: value === 'discord_join' ? 'join_discord' : value
        });
        break;
        
      default:
        // For other events, send a generic event to GA
        window.gtag('event', eventName, {
          ...metadata,
          value: value
        });
    }
  }

  /**
   * Track page views
   * @param {String} pageName - Name of the page being viewed
   * @param {Object} metadata - Additional metadata about the page
   */
  trackPageView(pageName, metadata = {}) {
    this.trackEvent(EVENTS.PAGE_VIEW, pageName, metadata);
  }
  
  /**
   * Track character selection
   * @param {String} characterId - ID of the selected character
   * @param {Object} metadata - Additional metadata about the character
   */
  trackCharacterSelected(characterId, metadata = {}) {
    this.trackEvent(EVENTS.CHARACTER_SELECTED, characterId, metadata);
  }
  
  /**
   * Track form interaction
   * @param {Object} metadata - Additional metadata about the form interaction
   */
  trackFormInteraction(metadata = {}) {
    this.trackEvent(EVENTS.FORM_INTERACTION, null, metadata);
  }
  
  /**
   * Track vote submission
   * @param {String} characterId - ID of the character voted for
   * @param {Object} metadata - Additional metadata about the vote
   */
  trackVoteSubmitted(characterId, metadata = {}) {
    this.trackEvent(EVENTS.VOTE_SUBMITTED, characterId, metadata);
  }

  /**
   * Track CTA modal interactions
   * @param {String} contestantId - ID of the contestant
   */
  trackCTAModalShown(contestantId) {
    this.trackEvent(EVENTS.CTA_MODAL_SHOWN, contestantId);
    this.modalOpenTime = new Date();
  }

  /**
   * Track CTA modal closed
   */
  trackCTAModalClosed() {
    const durationSeconds = this.modalOpenTime ? 
      Math.floor((new Date() - this.modalOpenTime) / 1000) : 0;
    
    this.trackEvent(EVENTS.CTA_MODAL_CLOSED, null, {
      modal_duration_seconds: durationSeconds
    });
  }

  /**
   * Track CTA button clicked
   * @param {String} buttonName - Name of the button clicked
   * @param {String} contestantId - ID of the contestant
   */
  trackCTAButtonClicked(buttonName, contestantId) {
    this.trackEvent(EVENTS.CTA_BUTTON_CLICKED, buttonName, {
      contestant_id: contestantId
    });
  }

  /**
   * Setup performance monitoring
   * @private
   */
  setupPerformanceMonitoring() {
    // Track page load performance
    if (window.performance && window.performance.timing) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const timing = window.performance.timing;
          const loadTime = timing.loadEventEnd - timing.navigationStart;
          
          this.trackEvent(EVENTS.INITIAL_LOAD_TIME, loadTime, {
            dns_time: timing.domainLookupEnd - timing.domainLookupStart,
            connect_time: timing.connectEnd - timing.connectStart,
            response_time: timing.responseEnd - timing.responseStart,
            dom_load_time: timing.domComplete - timing.domLoading,
            frontend_time: timing.loadEventEnd - timing.domComplete
          });
        }, 0);
      });
    }
    
    // Track image load errors
    window.addEventListener('error', (event) => {
      const target = event.target;
      if (target && target.tagName === 'IMG') {
        this.trackEvent(EVENTS.IMAGE_LOAD_ERROR, target.src, {
          image_url: target.src,
          error_message: event.message || 'Image load failed'
        });
      }
    }, true);
  }
}

// Create singleton instance
const unifiedAnalytics = new UnifiedAnalyticsService();

export default unifiedAnalytics;
